/* eslint-disable max-len */
import {
  blackListSlider,
  blackListSliderMobile,
} from '../utils/blackListSlider';
import { $Q, $Qll } from '../utils/query-selector';
import { createInterception } from '../utils/script-defer';
import { addTagScript } from '../utils/add-tag-script';

/**
 * Creates a slider using the Swiper library.
 * @param {Object} parent - An object containing the data necessary to build the slider.
 * @param {string} parent.slidesMobile - The number of slides to show on mobile devices.
 * @param {string} parent.slides - The number of slides to show on larger screens.
 * @param {string} parent.pagination - Whether or not to show pagination dots.
 * @param {string} parent.auto - Whether or not to automatically advance the slides.
 * @param {string} parent.delay - The time in milliseconds between automatic slide transitions.
 * @param {string} parent.script - The URL of an external script to load.
 * @param {string} parent.spacing - The amount of space between slides, in pixels.
 */
// eslint-disable-next-line max-lines-per-function, complexity
export const createSlider = (container) => {
  const PAGE_ONE = 1;
  const {
    slidesMobile,
    slides,
    pagination,
    navigation,
    auto,
    spacing,
    speed,
    freeMode,
    autoHeight,
    centered,
    initialSlide,
  } = container.dataset;

  let swiperParams = {
    slidesPerView:
      slidesMobile === 'auto' ? slidesMobile : Number(slidesMobile),
    spaceBetween: Number(spacing),
    initialSlide: initialSlide,
    centeredSlides: centered === 'true',
    loop: auto === 'true',
    ...(speed > 0 && {
      autoplay: {
        delay: Number(speed),
      },
    }),
    ...(freeMode === 'true' && {
      freeMode: {
        enabled: freeMode,
        momentumBounce: false,
      },
    }),
    autoHeight: autoHeight === 'true',
    breakpoints: {
      640: {
        slidesPerView:
          // eslint-disable-next-line no-nested-ternary
          slidesMobile === 'auto'
            ? slidesMobile
            : Number(slides) === PAGE_ONE
            ? Number(slides)
            : Number(slidesMobile) + 1,
      },
      1024: {
        slidesPerView: slides === 'auto' ? slides : Number(slides),
      },
    },
  };

  if (navigation === 'true') {
    swiperParams = loadNavigation(container, swiperParams);
  }

  if (pagination === 'true') {
    swiperParams = loadPagination(container, swiperParams);
  }

  Object.assign(container, swiperParams);
  return container.initialize();
};

const loadPagination = (slider, params) => {
  const paginationContainer = $Q('.swiper-pagination', slider.parentNode);
  if (!paginationContainer || !params) return;

  const mutationParams = { ...params };

  mutationParams['pagination'] = {
    el: paginationContainer,
    clickable: true,
  };

  return mutationParams;
};

const loadNavigation = (slider, params) => {
  const parent = slider.parentNode;
  if ($Qll('.swiper-button', parent).length < 2 || !params) return;
  const mutationParams = { ...params };

  const buttonNext = $Q('.swiper-button-next', parent);
  const buttonPrev = $Q('.swiper-button-prev', parent);

  mutationParams['navigation'] = {
    nextEl: buttonNext,
    prevEl: buttonPrev,
  };

  return mutationParams;
};

/**
A function that loads sliders on a page by
creating an intersection observer for each slider container element.
@returns {void}
*/
export const loadSlider = async () => {
  const vendor = $Q('#swiperScript');

  if (!vendor) {
    const firstSlider = $Q('.slider-js');
    if (!firstSlider) return;
    await addTagScript(firstSlider.dataset.script, 'swiperScript');
  }

  return forEachSlider();
};

const forEachSlider = () => {
  const dataSliders = $Qll('.slider-js');

  for (let i = 0; i < dataSliders.length; i++) {
    if (blackListSlider(dataSliders[i])) return;
    if (blackListSliderMobile(dataSliders[i])) return;

    createInterception(dataSliders[i], () => callbackSlider(dataSliders[i]));
  }
}

/**
 * this script only select one slider predefine. example at doing click button
 * @param {Node} slider - node slider
 */
export const loadSliderByEvent = (slider) => {
  createInterception(slider, () => callbackSlider(slider));
};

export const callbackSlider = async (slider) => {
  const loadScript = await addTagScript(slider.dataset.script, 'swiperScript');

  if (loadScript) {
    return createSlider(slider);
  }
}
